import { motion } from "framer-motion";
import { useState } from "react";
import { SEO } from "@/components/SEO";
import { Download, FileText, Filter } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GUIDES_DATA } from "@/lib/index";
import { IMAGES } from "@/assets/images";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const categorieLabels = {
  parents: 'Pour les parents',
  avocats: 'Pour les avocats',
  general: 'Général',
};

const categorieColors = {
  parents: 'bg-primary/10 text-primary',
  avocats: 'bg-secondary/10 text-secondary',
  general: 'bg-accent/10 text-accent-foreground',
};

export default function Guides() {
  const [selectedCategorie, setSelectedCategorie] = useState<string | null>(null);

  const filteredGuides = selectedCategorie
    ? GUIDES_DATA.filter(guide => guide.categorie === selectedCategorie)
    : GUIDES_DATA;

  const categories = Array.from(new Set(GUIDES_DATA.map(g => g.categorie)));

  return (
    <div className="min-h-screen">
      <SEO
        title="Feuillets téléchargeables — Guides pratiques pour les familles"
        description="Téléchargez gratuitement nos guides pratiques sur la coparentalité, la médiation familiale et le bien-être des enfants lors de la séparation, rédigés par nos experts en Outaouais."
        keywords="guides coparentalité téléchargement, feuillet médiation familiale PDF, ressources parents séparés Québec, plan de coparentalité, droits accès garde"
        canonical="/guides"
      />
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.CONSULTATION_2}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Badge className="mb-4">Guides pratiques</Badge>
            </motion.div>
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              variants={staggerItem}
            >
              Guides téléchargeables
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground max-w-3xl mx-auto"
              variants={staggerItem}
            >
              Téléchargez gratuitement nos guides pratiques pour vous préparer à vos démarches et mieux comprendre les processus. Des outils concrets pour vous accompagner.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            className="flex flex-wrap gap-3 justify-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <Button
              variant={selectedCategorie === null ? "default" : "outline"}
              onClick={() => setSelectedCategorie(null)}
            >
              <Filter className="mr-2 h-4 w-4" />
              Tous les guides
            </Button>
            {categories.map((categorie) => (
              <Button
                key={categorie}
                variant={selectedCategorie === categorie ? "default" : "outline"}
                onClick={() => setSelectedCategorie(categorie)}
              >
                {categorieLabels[categorie as keyof typeof categorieLabels]}
              </Button>
            ))}
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {filteredGuides.map((guide) => (
              <motion.div key={guide.id} variants={staggerItem}>
                <Card className="h-full hover:shadow-lg transition-all duration-200 group">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileText className="h-8 w-8 text-primary" />
                      </div>
                      <Badge className={categorieColors[guide.categorie as keyof typeof categorieColors]}>
                        {categorieLabels[guide.categorie as keyof typeof categorieLabels]}
                      </Badge>
                    </div>
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                      {guide.titre}
                    </h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      {guide.description}
                    </p>
                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-6">
                      <span className="font-medium">{guide.format}</span>
                      <span>{guide.taille}</span>
                    </div>
                    <Button className="w-full group-hover:bg-primary/90" asChild>
                      <a href={guide.urlTelechargement} download>
                        <Download className="mr-2 h-4 w-4" />
                        Télécharger
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <Card className="bg-accent/10 border-accent/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">Comment utiliser ces guides?</h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    <strong className="text-foreground">1. Téléchargez</strong> le guide correspondant à votre situation ou à vos besoins.
                  </p>
                  <p>
                    <strong className="text-foreground">2. Lisez attentivement</strong> les informations et conseils fournis.
                  </p>
                  <p>
                    <strong className="text-foreground">3. Préparez</strong> les documents et informations mentionnés dans le guide.
                  </p>
                  <p>
                    <strong className="text-foreground">4. Contactez-nous</strong> si vous avez des questions ou besoin d'un accompagnement personnalisé.
                  </p>
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-4">
                    Ces guides sont fournis à titre informatif et ne remplacent pas un accompagnement professionnel personnalisé.
                  </p>
                  <Button asChild>
                    <a href="#/contact">Prendre rendez-vous</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
